import React, { useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import useOffboardingItAccessApi from './config';
import { parseTableParams } from '../../utils/Utils';
import DataTable from '../../components/ui/DataTable';
import OptionsMenu from '../../components/ui/OptionsMenu';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import { useAuth } from '../../context/AuthContext';

const Container = styled.div``;

export default function OffboardingItAccessPage() {
  const { t } = useTranslation();
  const { canWrite } = useAuth();
  const writable = canWrite ? canWrite('OD') : false;

  const [queryParams, setQueryParams] = useState({ pageSize: 25, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);
  const [confirm, setConfirm] = useState(null);

  const { fetching, data, pagination, columns: baseColumns, revoke, verify, block } = useOffboardingItAccessApi(queryParams);

  const handleDataStateChange = useCallback((act, tableState) => {
    setFilterList(tableState.filterList);
    const params = parseTableParams(tableState, baseColumns);
    setQueryParams(params);
  }, [baseColumns]);

  const handleFilterConfirm = useCallback((newFilterList) => setFilterList(newFilterList), []);

  const buildActions = useCallback((row) => {
    if (!row || !writable) return [];
    const actions = [];
    if (row.status === 'pending' || row.status === 'blocked') {
      actions.push({
        type: 'edit',
        label: t('offboardingItAccess.action.revoke'),
        onClick: () => setConfirm({
          message: t('offboardingItAccess.confirm.revoke', { system: row.systemName }),
          onYes: () => revoke(row.id, ''),
        }),
      });
    }
    // Verify is offered to everyone with write access — including the revoker.
    // The four-eyes rule lives in the backend and only applies when the tenant
    // has another eligible verifier; hiding the button here made the remaining
    // verification work invisible in single-admin tenants (rows sat in
    // "revoked" with no visible next step, and the offboarding could never
    // advance — customer feedback). If the rule applies, the backend returns
    // the explanatory od.itAccess.verifierMustDifferFromRevoker toast.
    if (row.status === 'revoked') {
      actions.push({
        type: 'edit',
        label: t('offboardingItAccess.action.verify'),
        onClick: () => setConfirm({
          message: t('offboardingItAccess.confirm.verify', { system: row.systemName }),
          onYes: () => verify(row.id, ''),
        }),
      });
    }
    if (row.status === 'pending') {
      actions.push({
        type: 'delete',
        label: t('offboardingItAccess.action.block'),
        onClick: () => setConfirm({
          message: t('offboardingItAccess.confirm.block', { system: row.systemName }),
          onYes: () => block(row.id, t('offboardingItAccess.defaultBlockReason')),
        }),
      });
    }
    return actions;
  }, [t, writable, revoke, verify, block]);

  const columns = useMemo(() => {
    if (!writable) return baseColumns;
    return [
      ...baseColumns,
      {
        name: 'actions', label: ' ',
        options: {
          filter: false, sort: false, download: false, print: false,
          customBodyRender: (_, tableMeta) => {
            const row = data[tableMeta.rowIndex];
            const actions = buildActions(row);
            return actions.length > 0 ? <OptionsMenu actions={actions} row={row} /> : null;
          },
        },
      },
    ];
  }, [baseColumns, data, buildActions, writable]);

  return (
    <Container>
      {useMemo(() => (
        <DataTable
          title={t('offboardingItAccess.pageTitle')}
          fetching={fetching}
          columns={columns}
          data={data}
          totalRowCount={pagination.totalRowCount}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          filterList={filterList}
          onFilterConfirm={handleFilterConfirm}
          onTableChanged={handleDataStateChange}
        />
      ), [
        t, fetching, columns, data, pagination,
        filterList, handleFilterConfirm, handleDataStateChange,
      ])}
      <ConfirmDialog
        isOpen={!!confirm}
        message={confirm?.message}
        onConfirm={async () => { await confirm.onYes(); setConfirm(null); }}
        onCancel={() => setConfirm(null)}
      />
    </Container>
  );
}
